import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useDelayedFlag, useTran } from "@/hooks";
import { useCompanyStore } from "@/features/company/hooks";
import { useAuthStore } from "@/features/auth";
import {
  KEY_IMPROVEMENT,
  LOCATION_OPTIONS,
  INDUSTRIAL_CLASSIFICATION,
} from "@/lib/constants/options";
import { Building, BarChart3, Globe, Target, Info } from "lucide-react";
import CompanySnapshotSkeleton from "./skeleton/CompanySnapshotSkeleton";
import { Pencil } from "lucide-react";

const InfoRow = ({ label, value }) => {
  const sanitizedValue =
    value !== null && value !== undefined
      ? typeof value === "string"
        ? value
        : value.toString?.() || ""
      : "";

  return (
    <div className="flex items-center justify-between text-sm gap-3">
      <span className="tracking-wide text-muted-foreground">{label}</span>
      <span
        className="text-foreground max-w-[58%] truncate"
        title={sanitizedValue}
      >
        {value}
      </span>
    </div>
  );
};

const sanitizeItems = (items = []) =>
  items
    .map((item) => {
      if (!item) {
        return "";
      }
      if (typeof item === "string") {
        return item;
      }
      if (typeof item?.label === "string") {
        return item.label;
      }
      if (typeof item?.value === "string") {
        return item.value;
      }
      return "";
    })
    .map((value) => value.trim())
    .filter(Boolean);

const OptionPills = ({ items, fallback }) => {
  const filtered = sanitizeItems(items);
  if (filtered.length === 0) {
    return <span className="text-xs text-muted-foreground">{fallback}</span>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {filtered.map((label) => (
        <Badge key={label} variant="outline">
          {label}
        </Badge>
      ))}
    </div>
  );
};

const SectionLabel = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-sm font-medium">
    <Icon size={16} className="text-sky-600" />
    <span className="text-sky-600">{label}</span>
  </div>
);

const resolveLabel = (value, options, fallback) => {
  if (!value) return fallback;
  const found = options.find((option) => option.key === value);
  if (found) {
    return found.label || found.value || fallback;
  }
  return value
    .toString()
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (chr) => chr.toUpperCase());
};

const CompanySnapshotCard = observer(({ onEdit }) => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const { company, form, companyLoading, fetchCompany } = useCompanyStore();
  const showSkeleton = useDelayedFlag(companyLoading);

  const userCompanyId =
    user?.companyId ||
    user?.company?._id ||
    user?.company?.id ||
    user?.company?.companyId ||
    null;

  useEffect(() => {
    if (!userCompanyId || company || companyLoading) return;
    fetchCompany({ companyId: userCompanyId });
  }, [company, companyLoading, fetchCompany, userCompanyId]);

  const notSet = t("Not set");
  const companyName =
    company?.companyName || company?.name || form.companyName || notSet;
  const country = resolveLabel(
    company?.country || form.country,
    LOCATION_OPTIONS,
    notSet,
  );
  const isOrg = Boolean(company?.organizationUnitId);

  const industry = useMemo(
    () =>
      resolveLabel(
        company?.industry || form.industry,
        INDUSTRIAL_CLASSIFICATION,
        notSet,
      ),
    [company?.industry, form.industry, notSet],
  );

  const employees = company?.employeesRange || form.employeesRange || notSet;
  const revenue = company?.revenueRange || form.revenueRange || notSet;

  const serviceCountries = useMemo(() => {
    const values = company?.serviceCountries || form.serviceCountries || [];
    if (!Array.isArray(values)) return [];
    return values.map((value) =>
      resolveLabel(value, LOCATION_OPTIONS, value || notSet),
    );
  }, [company?.serviceCountries, form.serviceCountries, notSet]);

  const objectives = useMemo(() => {
    const values = company?.objectives || form.objectives || [];
    if (!Array.isArray(values)) return [];
    return values.map((value) =>
      resolveLabel(value, KEY_IMPROVEMENT, value || notSet),
    );
  }, [company?.objectives, form.objectives, notSet]);

  return (
    <Card className="rounded-radius pt-0 h-fit self-start">
      <CardHeader className="rounded-t-lg grid gap-2 py-3 bg-gradient-to-br from-slate-100 to-white dark:from-white/10 dark:to-white/5">
        <CardTitle className="flex items-center gap-2 text-base text-primary">
          <Building size={20} />
          {t("Company Snapshot")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {companyLoading && !showSkeleton ? (
          <p className="text-xs text-muted-foreground">
            {t("Syncing latest company details...")}
          </p>
        ) : null}

        {showSkeleton ? (
          <CompanySnapshotSkeleton />
        ) : (
          <div className="space-y-4">
            <div>
              <SectionLabel icon={Info} label={t("Company Information")} />
              <div className="mt-2 space-y-2 rounded-radius border border-border p-3">
                <InfoRow label={t("Name")} value={companyName} />
                <InfoRow label={t("Country")} value={country} />
                <InfoRow
                  label={isOrg ? t("Parent Unit") : t("Industry")}
                  value={
                    isOrg
                      ? company?.parentUnitName || notSet
                      : industry || notSet
                  }
                />
              </div>
            </div>

            <div>
              <SectionLabel icon={BarChart3} label={t("Size & Revenue")} />
              <div className="mt-2 space-y-2 rounded-radius border border-border p-3">
                <InfoRow
                  label={isOrg ? t("Unit Level") : t("Employees")}
                  value={isOrg ? company?.unitLevel || notSet : employees}
                />
                <InfoRow
                  label={isOrg ? t("Unit Type") : t("Revenue (USD)")}
                  value={isOrg ? company?.unitType || notSet : revenue}
                />
              </div>
            </div>

            {!isOrg ? (
              <div>
                <SectionLabel icon={Globe} label={t("Service Countries")} />
                <div className="mt-2 rounded-radius border border-border p-3">
                  <OptionPills items={serviceCountries} fallback={notSet} />
                </div>
              </div>
            ) : null}

            {!isOrg ? (
              <div>
                <SectionLabel
                  icon={Target}
                  label={t("Key Improvement Objectives")}
                />
                <div className="mt-2 rounded-radius border border-border p-3">
                  <OptionPills items={objectives} fallback={notSet} />
                </div>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-end">
        {showSkeleton ? (
          <Skeleton className="h-9 w-36" />
        ) : (
          <Button icon={Pencil} onClick={onEdit}>
            {t("Edit Company Setup")}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
});

export default CompanySnapshotCard;
